import { Value } from 'classnames';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetProductVariantColumn from 'hooks/use-get-product-variant-column';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewPurchase } from '../../hooks/use-get-default-values';
import {
  ProductPurchaseDetailFormType,
  ProductPurchaseFormType,
} from '../form-type';

function useGetProductVariantTableList(): IColumn<ProductPurchaseDetailFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const productVariantColumn = useGetProductVariantColumn(false);

  return [
    {
      header: t('product:product-variant'),
      accessorKey: 'productVariantId',
      minSize: 400,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: productVariantColumn,
    },
    {
      header: t('product:price_per_item'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
    {
      header: t('common:quantity'),
      accessorKey: 'qty',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
    {
      header: t('common:description'),
      accessorKey: 'description',
      minSize: 100,
      dataType: ColumnDataType.text,
    },

    ...deleteColumn(editable),
  ];
}

export default function ProductVariantTable() {
  const { editable } = useFormState();
  const columns = useGetProductVariantTableList();
  const { control, trigger, getValues, clearErrors } =
    useFormContext<ProductPurchaseFormType>();

  const name = 'productPurchases' as const;

  const { fields, update, remove, append } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const columnId = action.columnId as keyof ProductPurchaseDetailFormType;
      const type = action.type as ActionType;
      const value = action.value;
      const extra = action.extra as Value;
      const currentRowIndex = action.rowIndex as number;
      const items = getValues(name);
      const item = items[currentRowIndex];

      const otherVal: () => Partial<ProductPurchaseDetailFormType> = () => {
        if (columnId === 'productVariantId') {
          return {
            price: extra?.[0]?.extra?.sellPrice,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewPurchase());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...item,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <SeparatorVertical $margin={36} />
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
